export interface Feature {
  description: string;
  /**
   * Optionally do something when the feature is enabled, like display helpful information in
   * console.log.
   */
  introduction?: () => void;
  // We could add fields like "allowedUsers" or other fancy things as-needed.
}

/** Helper type that takes a repository and returns the corresponding feature IDs. */
export type FeatureFlagIds<T> = T extends FeatureFlagsRepository<infer X> ? X : never;

export class FeatureFlagsRepository<FeatureId extends string> {
  protected static defaultFlags = {};

  protected listeners: Array<() => void> = [];

  protected currentFlags: Record<FeatureId, boolean>;

  constructor(
    protected store: Storage,
    protected flagDefinitions: Record<FeatureId, Feature>,
  ) {
    this.currentFlags = this.restore() || FeatureFlagsRepository.defaultFlags;
  }

  /** Returns the previous session from storage, or returns null. */
  restore() {
    const serialized = this.store.getItem( 'feature-flags' );

    if ( serialized ) {
      return JSON.parse( serialized );
    }

    return null;
  }

  /**
   * Get the actual flags obejct. You generally shouldn't use this. It's for testing.
   */
  getObject() {
    return this.currentFlags;
  }

  /** Listen to changes, and get back a destructor that stops listening. */
  listen( listener: () => void ) {
    this.listeners.push( listener );

    return () => {
      this.listeners = this.listeners.filter( ( l ) => l !== listener );
    };
  }

  save() {
    this.store.setItem( 'feature-flags', JSON.stringify( this.currentFlags ) );
  }

  emit() {
    this.listeners.forEach( ( listener ) => listener() );
  }

  resetAll() {
    Object.keys( this.currentFlags ).forEach( ( key ) => {
      delete this.currentFlags[key as FeatureId];
    } );
  }

  enable( featureName: FeatureId ) {
    this.currentFlags[featureName] = true;
    this.save();
    this.emit();
  }

  disable( featureName: FeatureId ) {
    this.currentFlags[featureName] = false;
    this.save();
    this.emit();
  }

  reset( featureName: FeatureId ) {
    delete this.currentFlags[featureName];
    this.save();
    this.emit();
  }

  check( feature: FeatureId, defaultValue: boolean = false ): boolean {
    return feature in this.currentFlags && this.currentFlags[feature] !== null
      ? this.currentFlags[feature]
      : defaultValue;
  }

  list() {
    const allFeatureFlags = Object.fromEntries(
      Object.keys( this.flagDefinitions ).map( ( key ) => {
        const flag = this.flagDefinitions[key as FeatureId];
        return [key, `${this.check( key as FeatureId ) ? '[ON]' : '[OFF]'} ${flag.description}`];
      } ),
    );

    console.log( 'Feature Flags:' );
    console.table( allFeatureFlags );
  }
}
